/**
 * PATH: packages/bills/src/feature/billsLayoutPrefs.ts
 * PURPOSE: Billing-hub layout prefs — where the master shell nav docks.
 * WHY: The 3-pane bookkeeping workspace needs every horizontal pixel.
 *      Operators asked to dock the master left rail to the bottom on
 *      billing routes (Email Hub already supports this pattern).
 * FLOW: read → localStorage → BillingHubLayout menu / UnifiedLayout shell
 */

export type BillsMasterNavPosition = 'left' | 'bottom'

export const BILLS_LAYOUT_CHANGED_EVENT = 'quotech:bills-layout-changed'

const LS_KEY = 'quotech_bills_layout_v1'

export interface BillsLayoutPrefs {
  /** Where the master DashboardLayout sidebar lives on /bills/* routes. */
  masterNavPosition: BillsMasterNavPosition
}

const DEFAULTS: BillsLayoutPrefs = {
  // Default (2026-07-02): keep the master left rail by default so the left
  // sidenav never disappears on /bills for a fresh user. The bottom-dock
  // option (which frees ~240px for the list · PDF · data 3-pane) stays
  // available on demand via the BillsLayoutMenu — it's just no longer the
  // default, which had hidden the entire left sidenav for everyone.
  masterNavPosition: 'left',
}

function coerce(raw: Partial<BillsLayoutPrefs> | null | undefined): BillsLayoutPrefs {
  const pos = raw?.masterNavPosition
  return {
    masterNavPosition: pos === 'left' || pos === 'bottom' ? pos : DEFAULTS.masterNavPosition,
  }
}

export function readBillsLayoutPrefs(): BillsLayoutPrefs {
  if (typeof window === 'undefined') return { ...DEFAULTS }
  try {
    const raw = window.localStorage.getItem(LS_KEY)
    if (!raw) return { ...DEFAULTS }
    return coerce(JSON.parse(raw) as Partial<BillsLayoutPrefs>)
  } catch {
    return { ...DEFAULTS }
  }
}

export function writeBillsLayoutPrefs(next: BillsLayoutPrefs): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(coerce(next)))
    window.dispatchEvent(new CustomEvent(BILLS_LAYOUT_CHANGED_EVENT, { detail: next }))
  } catch {
    /* private mode / quota — fail soft */
  }
}

export function isOnBillsRoute(pathname: string): boolean {
  return pathname === '/bills' || pathname.startsWith('/bills/')
}
